import { isDefined, isNotDefined } from '@sgme/fp';
import type {
  Column,
  ColumnEvent,
  ColumnGroupOpenedEvent,
  ColumnMovedEvent,
  ColumnPinnedEvent,
  ColumnResizedEvent,
  ColumnVisibleEvent,
  FilterChangedEvent,
  FilterModel,
  FilterModifiedEvent,
  GridApi,
  SortChangedEvent,
} from 'ag-grid-community';
import { useCallback, useEffect } from 'react';
import type { IBlotterColumnDefinition } from 'state/blotters/common/blotter.model';
import { logFrontError } from 'utils/errors/logFrontError';

import type { ColumnGroupPreferenceState, ColumnPreferenceState } from '../blotter.model';
import { extractCurrentColumnsFilterState, sanitizedFilterModel } from './utils';

function isColumnEvent(event: any): event is ColumnEvent {
  return (event as ColumnEvent).source !== undefined;
}
function getColumnEventSource(event: any) {
  if (isColumnEvent(event)) {
    return event.source;
  }
  return undefined;
}

type ColumnChangedEvent =
  | ColumnEvent
  | ColumnMovedEvent
  | ColumnResizedEvent
  | ColumnPinnedEvent
  | ColumnVisibleEvent
  | SortChangedEvent
  | ColumnGroupOpenedEvent
  | FilterChangedEvent
  | FilterModifiedEvent;

export function sanitize(columns: readonly Column[], currentColumnsFilterState: Record<string, unknown>) {
  const result = columns.reduce(
    (acc, col) => {
      const colId = col.getColId();
      const colFilterDef = col.getColDef().filter;
      const colFilterState = currentColumnsFilterState[colId] as any;

      if (!colFilterState || !colFilterDef) {
        return acc;
      }

      if (
        (colFilterDef === 'agMultiColumnFilter' && colFilterState.filterType !== 'multi') ||
        (colFilterState.filterType === 'multi' && colFilterDef !== 'agMultiColumnFilter')
      ) {
        return acc;
      }

      acc[colId] = colFilterState;
      return acc;
    },
    {} as Record<string, any>,
  );
  return result;
}

export function useApplyColumnSettings(
  gridApi: GridApi | undefined,
  currentColumns: readonly ColumnPreferenceState[] | undefined,
  currentColumnGroups?: readonly ColumnGroupPreferenceState[] | undefined,
  columnDefinitions?: readonly IBlotterColumnDefinition[],
  filterModelSanitizer: (
    allColumns: readonly any[],
    currentColumnsFilterState: Record<string, unknown>,
  ) => FilterModel | null = sanitizedFilterModel,
) {
  useEffect(() => {
    if (isNotDefined(gridApi)) {
      return;
    }

    if (isDefined(currentColumns)) {
      const allColumns = gridApi.getAllGridColumns();
      const currentColumnsFilterState = extractCurrentColumnsFilterState(currentColumns);
      const filterModel = filterModelSanitizer(allColumns ?? [], currentColumnsFilterState);

      if (filterModel !== null) {
        try {
          gridApi.setFilterModel(filterModel);
        } catch (error) {
          logFrontError('An error was caught when loading grid filters {@FrontError}', error);
        }
        gridApi.applyColumnState({
          state: currentColumns as ColumnPreferenceState[],
          applyOrder: true,
        });
      }
    }
    if (isDefined(currentColumnGroups)) {
      const state = currentColumnGroups.map(c => ({ groupId: c.groupId, open: c.isExpanded }));
      gridApi.setColumnGroupState(state);
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps apply effect also when columnDefinitions change
  }, [currentColumnGroups, gridApi, currentColumns, columnDefinitions]);
}

export function usePersistColumnState(
  saveColumns: (
    newColumns: readonly ColumnPreferenceState[],
    newColumnGroups?: readonly ColumnGroupPreferenceState[],
  ) => void,
  maintainColumnOrder?: boolean,
) {
  const onColumnChanged = useCallback(
    (event: ColumnChangedEvent) => {
      const { api } = event;

      // Wait event is finished before applying it e.g: we want to persist columns positions when the column are finished to be moved
      if ('finished' in event && !event.finished) {
        return;
      }
      const eventSource = getColumnEventSource(event);
      // Ignore all events raised from the rows updated by restoring previous columns state, we don't care about columns changed in this case
      if (eventSource === 'rowDataUpdated' || eventSource === 'api') {
        return;
      }
      const newColumnState = api.getAllGridColumns().map(col => {
        const colId = col.getColId();
        const filterModel = api.getColumnFilterModel(colId);
        return {
          colId: maintainColumnOrder ? col.getColDef().headerName : colId,
          width: col.getActualWidth(),
          pinned: col.getPinned(),
          hide: !col.isVisible(),
          sort: col.getSort(),
          filterModel,
        } as ColumnPreferenceState;
      });

      const newColumnGroupState = api
        .getColumnGroupState()
        .map(({ groupId, open }) => ({ groupId, isExpanded: open }) as ColumnGroupPreferenceState);

      saveColumns(newColumnState, newColumnGroupState);
      return;
    },
    [saveColumns, maintainColumnOrder],
  );

  return onColumnChanged;
}
